import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useTabNavigation } from 'src/redux/slices/navigation/hooks'
import { getOrganizationInvites, updateUserInOrganization } from 'src/redux/slices/user/endpoints'
import { activateOrganization } from 'src/redux/slices/user/reducers'
import { Organization, UserInOrganization } from 'src/types/database-model'

import {
  AccordionActions,
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import Accordion from '@material-ui/core/Accordion'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import ExpandMore from '@material-ui/icons/ExpandMore'
import FolderOpenIcon from '@material-ui/icons/FolderOpen'

import InviteUser from './InviteUser'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    accordionRoot: {
      width: '100%',
    },
    projectsButton: {
      marginLeft: 'auto',
    },
    divider: {
      margin: '0.5rem 0',
    },
  }),
)

///// Types
type TeamOverviewProps = Organization & {
  members: UserInOrganization[]
  organization_id: number
}

type ChangeUserRoleArg = {
  user_id: string
  role: string
  role_type: 'access' | 'sustainability'
}

///// Component
const TeamOverview: React.FC<TeamOverviewProps> = ({ title, members, organization_id }) => {
  const dispatch = useDispatch()
  const { goToTab } = useTabNavigation()
  const { data: pendingInvites, isLoading } = getOrganizationInvites.useQuery({
    organization_id,
  })

  const makeActiveOrganization = useCallback(() => {
    dispatch(activateOrganization(organization_id))
    goToTab('projects')
  }, [organization_id])

  const classes = useStyles()

  const [updateUser] = updateUserInOrganization.useMutation()

  const changeUserRole = useCallback(
    ({ user_id, role, role_type }: ChangeUserRoleArg) => {
      updateUser({
        organization_id,
        user_id,
        update: {
          [role_type === 'access' ? 'access_role' : 'sustainability_role']: role,
        },
      })
    },
    [updateUser, organization_id],
  )

  return (
    <Grid item xs={12} md={6} key={`Organization-${organization_id}-${title}`}>
      <Accordion className={classes.accordionRoot} defaultExpanded={true}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant='body1'>{title}</Typography>
          <Button
            className={classes.projectsButton}
            variant='outlined'
            color='primary'
            endIcon={<FolderOpenIcon />}
            onClick={makeActiveOrganization}
            size='small'
          >
            Projects
          </Button>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container justifyContent='space-between' spacing={2}>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            {members.map(({ sustainability_role, access_role, user_id: user }, j) => (
              <Grid item container xs={12} spacing={1} key={`user-${user.id}`} alignItems='center'>
                {j !== 0 && (
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                )}

                <Grid item xs={4}>
                  <Typography noWrap variant='body1' align='center'>
                    {/* Yuck... just truncating the username, or email if user has no name*/}
                    {(user?.name ?? user?.email ?? '').length > 14
                      ? `${(user.name ?? user.email)?.substring(0, 14)}...`
                      : user.name ?? user.email}
                  </Typography>
                </Grid>

                <Grid item xs={8}>
                  <TextField
                    select
                    label='Team Role'
                    value={sustainability_role}
                    fullWidth
                    size='small'
                    onChange={(e) =>
                      changeUserRole({
                        role: e.target.value,
                        role_type: 'sustainability',
                        user_id: user.id,
                      })
                    }
                  >
                    {['Champion', 'Energy Modeler', 'GIS Manager'].map((role) => (
                      <MenuItem key={`role-option-${role}`} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {/* <Grid item xs={4}>
                  <TextField
                    select
                    label='Access Role'
                    value={access_role}
                    fullWidth
                    size='small'
                    onChange={(e) =>
                      changeUserRole({
                        role: e.target.value,
                        role_type: 'access',
                        user_id: user.id,
                      })
                    }
                  >
                    {['Admin', 'Editor', 'Viewer'].map((role) => (
                      <MenuItem key={`access-role-option-${role}`} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid> */}
              </Grid>
            ))}
            {(pendingInvites ?? []).length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item container xs={12}>
                  <Grid item xs={12}>
                    {pendingInvites?.map(({ email }, i) => (
                      <Typography variant='caption' key={`pending-invite-${i}-${email}`}>
                        {email} (Pending)
                      </Typography>
                    ))}
                  </Grid>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <InviteUser organization_id={organization_id} />
            </Grid>
          </Grid>
        </AccordionDetails>
        {/* <Divider />
        <AccordionActions>
          <InviteUser organization_id={organization_id} />
          <Button
            className={classes.projectsButton}
            variant='outlined'
            color='primary'
            endIcon={<FolderOpenIcon />}
            onClick={makeActiveOrganization}
            size='small'
          >
            Projects
          </Button>
        </AccordionActions> */}
      </Accordion>
      {/* <Grid item xs={12}>
        <Typography variant='h5'>{title}</Typography>
      </Grid>

      <Grid item xs={12}>
        {members.map(({ sustainability_role, access_role, user_id: user }, j) => (
          <Typography key={`member-${organization_id}-${j}`}>
            {user.name} // {sustainability_role} ({access_role})
          </Typography>
        ))}
      </Grid>
      <Grid item xs={12}>
        <Divider flexItem={true} />
      </Grid>
      <Grid item xs={12}>
        {pendingInvites?.map(({ email }, i) => (
          <Typography key={`pending-invite-${i}-${email}`}>{email} (Pending)</Typography>
        ))}
      </Grid>

      <InviteUser organization_id={organization_id} />
      <Grid item>
        <Button
          variant='outlined'
          color='primary'
          endIcon={<FolderOpenIcon />}
          onClick={makeActiveOrganization}
        >
          Projects
        </Button>
      </Grid> */}
    </Grid>
  )
}

export default TeamOverview
